import React from 'react';
import { Card as MuiCard, CardContent, CardHeader, CardActions, Typography, Box, useTheme } from '@mui/material';

/**
 * Card Component
 * A reusable card component that wraps Material-UI Card with additional features
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Card content
 * @param {string} props.title - Card title
 * @param {React.ReactNode} props.subtitle - Card subtitle
 * @param {React.ReactNode} props.icon - Icon to display in the header
 * @param {React.ReactNode} props.headerAction - Action component to display in the header
 * @param {React.ReactNode} props.actions - Actions to display in the footer
 * @param {Object} props.sx - Additional styles for the card
 * @param {boolean} props.elevated - Whether to elevate the card on hover
 * @param {string} props.status - Status indicator (success, warning, error, info)
 * @returns {React.ReactElement} Styled card component
 */
const Card = ({
  children,
  title,
  subtitle,
  icon,
  headerAction,
  actions,
  sx = {},
  elevated = false,
  status,
  ...rest
}) => {
  const theme = useTheme();
  
  // Status colors
  const statusColors = {
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
  };
  
  // Combine styles based on props
  const cardStyles = {
    position: 'relative',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px', // Set explicit 4px border-radius
    boxShadow: 'none',
    ...(status && {
      borderLeft: `4px solid ${statusColors[status] || theme.palette.primary.main}`,
    }),
    ...sx,
  };
  
  // Render the card
  return (
    <MuiCard sx={cardStyles} {...rest}>
      {/* Card Header */}
      {(title || icon) && (
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              {icon}
              <Typography variant="h6" component="div">
                {title}
              </Typography>
            </Box>
          }
          subheader={subtitle}
          action={headerAction}
          sx={{ p: 1 }}
        />
      )}
      
      {/* Card Header action if no title/icon but action exists */}
      {!title && !icon && headerAction && (
        <Box sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}>
          {headerAction}
        </Box>
      )}
      
      {/* Card Content */}
      <CardContent sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {children}
      </CardContent>
      
      {/* Card Actions */}
      {actions && (
        <CardActions sx={{ justifyContent: 'flex-end', padding: 2 }}>
          {actions}
        </CardActions>
      )}
    </MuiCard>
  );
};

export default Card;